import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Workflow as WorkflowIcon } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from '../../components/CustomToast';
import {
  listWorkflows,
  createWorkflow,
  deleteWorkflow,
  Workflow,
} from './api';
import { AutomationCard } from './automation-card';
import { ConfirmModal } from '../../components/ui/confirm-modal';
import { Dropdown, DropdownOption } from '../../components/ui/dropdown';
import { cn } from '../../lib/utils';

type StatusFilter = 'all' | 'published' | 'draft';
type WorkflowSort = 'updated_desc' | 'updated_asc' | 'name_asc' | 'name_desc' | 'steps_desc';

const SORT_OPTIONS: DropdownOption<WorkflowSort>[] = [
  { value: 'updated_desc', label: 'Recently Updated' },
  { value: 'updated_asc', label: 'Oldest' },
  { value: 'name_asc', label: 'Name: A to Z' },
  { value: 'name_desc', label: 'Name: Z to A' },
  { value: 'steps_desc', label: 'Most Steps' },
];

export function AutomationListPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [sort, setSort] = useState<WorkflowSort>('updated_desc');
  const [pendingDelete, setPendingDelete] = useState<{
    id: string;
    name: string;
  } | null>(null);

  // 1. Fetch Workflows List
  const {
    data: allWorkflows = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ['call-workflows'],
    queryFn: () => listWorkflows(),
  });

  // Only show active workflows (exclude legacy soft-archived items)
  const workflows = useMemo(
    () => allWorkflows.filter((w) => w.status !== 'archived'),
    [allWorkflows]
  );

  // A 403 here means the caller lacks workflows.view (default-off grant)
  const listForbidden = (error as { status?: number } | null)?.status === 403;

  // 2. Mutations
  const createMutation = useMutation({
    mutationFn: () => createWorkflow({ name: 'Untitled Workflow' }),
    onSuccess: (newWf) => {
      queryClient.invalidateQueries({ queryKey: ['call-workflows'] });
      navigate(`/home/create-automation?id=${newWf.id}`);
    },
    onError: (e: any) => toast.error(e?.message || 'Could not create the workflow.'),
  });

  const createBlankWorkflow = () => {
    if (createMutation.isPending) return;
    createMutation.mutate();
  };

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteWorkflow(id, true),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['call-workflows'] });
      setPendingDelete(null);
      toast.success('Workflow deleted successfully.');
    },
    onError: (e: any) => {
      toast.error(e?.message || 'Failed to delete workflow.');
    },
  });

  // Counts for filter pills
  const counts = useMemo(() => {
    let published = 0;
    let draft = 0;

    for (const wf of workflows) {
      if (wf.status === 'published') published++;
      else draft++;
    }

    return {
      all: workflows.length,
      published,
      draft,
    };
  }, [workflows]);

  // Filtering & Sorting
  const processedWorkflows = useMemo(() => {
    let list = workflows;

    // Status filter
    if (statusFilter === 'published') {
      list = list.filter((w) => w.status === 'published');
    } else if (statusFilter === 'draft') {
      list = list.filter((w) => w.status !== 'published');
    }

    // Search filter
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (wf) =>
          (wf.name || '').toLowerCase().includes(q) ||
          (wf.description || '').toLowerCase().includes(q)
      );
    }

    // Sort
    return [...list].sort((a, b) => {
      if (sort === 'name_asc') {
        return (a.name || '').localeCompare(b.name || '');
      }
      if (sort === 'name_desc') {
        return (b.name || '').localeCompare(a.name || '');
      }
      if (sort === 'steps_desc') {
        return (b.nodes?.length || 0) - (a.nodes?.length || 0);
      }
      if (sort === 'updated_asc') {
        const tA = new Date(a.updated_at || a.created_at || 0).getTime();
        const tB = new Date(b.updated_at || b.created_at || 0).getTime();
        return tA - tB;
      }
      // default: updated_desc
      const tA = new Date(a.updated_at || a.created_at || 0).getTime();
      const tB = new Date(b.updated_at || b.created_at || 0).getTime();
      return tB - tA;
    });
  }, [workflows, statusFilter, search, sort]);

  const filterTabs: { id: StatusFilter; label: string; count: number }[] = [
    { id: 'all', label: 'All', count: counts.all },
    { id: 'published', label: 'Published', count: counts.published },
    { id: 'draft', label: 'Drafts', count: counts.draft },
  ];

  return (
    <div className="flex flex-1 min-h-0 flex-col space-y-4 px-4 sm:px-5 md:px-6 pb-6 pt-3 sm:pt-4 w-full">
      {/* Header Row: Standard Dashboard Clean Layout */}
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-4 pb-2 border-b border-line">
        <div>
          <div className="flex items-center gap-2.5">
            <h1 className="text-sm font-semibold text-ink">Workflows</h1>
            <span className="rounded-full bg-soft border border-line px-2.5 py-0.5 text-xs font-bold text-ink-2">
              {workflows.length} Total
            </span>
          </div>

        </div>

        <button
          onClick={createBlankWorkflow}
          disabled={createMutation.isPending || listForbidden}
          title={listForbidden ? 'You do not have access to workflows' : undefined}
          className="flex min-h-[38px] items-center gap-2 rounded-xl bg-accent-500 px-4 py-2 text-sm font-semibold text-ink hover:bg-accent-600 transition-colors disabled:opacity-50"
        >
          <Plus className="h-4 w-4" />
          {createMutation.isPending ? 'Creating…' : 'New Workflow'}
        </button>
      </div>

      {/* Toolbar: Search, Status Filter Tabs, and Sort */}
      <div className="flex flex-col gap-4">
        {/* Status Filter Tabs */}
        <div className="flex items-center gap-2 overflow-x-auto pb-1 max-w-full">
          {filterTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setStatusFilter(tab.id)}
              className={cn(
                'flex items-center gap-1.5 rounded-full px-3.5 py-1 text-xs font-semibold transition-all',
                statusFilter === tab.id
                  ? 'bg-accent-500 text-ink shadow-sm'
                  : 'bg-soft text-ink-2 hover:bg-line hover:text-ink'
              )}
            >
              <span>{tab.label}</span>
              <span
                className={cn(
                  'rounded-full px-1.5 py-0.2 text-[10px] font-bold',
                  statusFilter === tab.id ? 'bg-black/15 text-ink' : 'bg-line/70 text-ink-3'
                )}
              >
                {tab.count}
              </span>
            </button>
          ))}
        </div>

        {/* Search and Sort Row */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="relative w-full max-w-md">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-3" />
            <input
              type="text"
              placeholder="Search workflows by name or description..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full rounded-xl border border-line bg-elevated py-2 pl-10 pr-4 text-sm text-ink shadow-2xs focus:border-brand focus:outline-none placeholder:text-ink-3"
            />
          </div>

          <div className="flex items-center gap-3">
            <div className="w-48">
              <Dropdown
                ariaLabel="Sort workflows"
                value={sort}
                options={SORT_OPTIONS}
                onChange={setSort}
                className="h-9 bg-elevated py-1.5 text-xs font-semibold"
              />
            </div>
            <span className="text-xs font-semibold text-ink-3 hidden sm:inline-block">
              Showing {processedWorkflows.length} of {workflows.length} workflows
            </span>
          </div>
        </div>
      </div>

      {/* Workflows Cards Grid */}
      {isLoading ? (
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-56 animate-pulse rounded-2xl bg-soft" />
          ))}
        </div>
      ) : listForbidden ? (
        <div className="rounded-2xl border border-dashed border-line bg-elevated p-16 text-center">
          <WorkflowIcon className="mx-auto h-10 w-10 text-ink-3 mb-3" />
          <h3 className="text-sm font-bold text-ink-2">No access to workflows</h3>
          <p className="text-xs text-ink-3 mt-1">Ask your organization owner for workflows access.</p>
        </div>
      ) : processedWorkflows.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-line bg-elevated p-16 text-center">
          <WorkflowIcon className="mx-auto h-10 w-10 text-ink-3 mb-3" />
          <h3 className="text-sm font-bold text-ink-2">No workflows found</h3>
          <p className="text-xs text-ink-3 mt-1">
            {search
              ? 'Adjust your search query or clear the filter.'
              : statusFilter !== 'all'
              ? `No ${statusFilter} workflows available.`
              : 'Create a new workflow to get started.'}
          </p>
          {statusFilter !== 'all' && (
            <button
              onClick={() => setStatusFilter('all')}
              className="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-line bg-soft px-3 py-1.5 text-xs font-semibold text-ink hover:bg-line transition-colors"
            >
              Show all workflows
            </button>
          )}
          {statusFilter === 'all' && (
            <button
              onClick={createBlankWorkflow}
              disabled={createMutation.isPending || listForbidden}
              className="mt-4 inline-flex min-h-[40px] items-center gap-2 rounded-xl bg-brand px-4 py-2 text-xs font-semibold text-white shadow-sm hover:bg-brand-hover transition-colors disabled:opacity-50"
            >
              <Plus className="h-3.5 w-3.5" />
              {createMutation.isPending ? 'Creating…' : 'Create Workflow'}
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
          {processedWorkflows.map((wf) => (
            <AutomationCard
              key={wf.id}
              workflow={wf}
              canEdit={!listForbidden}
              onDelete={(w) =>
                setPendingDelete({
                  id: w.id,
                  name: w.name,
                })
              }
            />
          ))}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {pendingDelete && (
        <ConfirmModal
          title="Delete Workflow"
          body={`Are you sure you want to delete "${pendingDelete.name}"? This will permanently delete the workflow.`}
          confirmLabel={deleteMutation.isPending ? 'Deleting…' : 'Delete Workflow'}
          onConfirm={() => deleteMutation.mutate(pendingDelete.id)}
          onClose={() => setPendingDelete(null)}
        />
      )}
    </div>
  );
}
